import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Search,
  Filter,
  Star,
  Workflow,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Rocket,
} from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Tabs, TabsList, TabsTrigger } from '../components/Tabs';
import { Skeleton } from '../components/Skeleton';
import { CreateSessionDialog } from '../components/CreateSessionDialog';
import { SessionCard } from '../components/SessionCard';
import { N8nPanel } from '../components/N8nPanel';
import { SessionNavToolbar } from '../components/SessionNavToolbar';
import { useSessionsList } from '../helpers/useSessionsApi';
import { useFavorites } from '../helpers/useFavoritesApi';
import { SessionStatus } from '../helpers/schema';
import { useTranslation } from '../helpers/useLocalization';
import { useIsMobile } from '../helpers/useIsMobile';
import styles from './dashboard.module.css';
import { useAuth } from '../helpers/useAuth';
import { OnboardingTutorial, useShowDashboardOnboarding } from '../components/OnboardingTutorial';

export default function DashboardPage() {
  const { authState } = useAuth();
  const currentUserId = authState.type === 'authenticated' ? authState.user?.id : null;
  const showTutorial = useShowDashboardOnboarding(currentUserId);
  const [tutorialDismissed, setTutorialDismissed] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isN8nPanelOpen, setIsN8nPanelOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [panelSide, setPanelSide] = useState<'left' | 'right'>('right');
  const isMobile = useIsMobile();

  useEffect(() => {
    const saved = localStorage.getItem('tasking_panel_side') as 'left' | 'right' | null;
    if (saved) setPanelSide(saved);
  }, []);

  const { data: favoritesData, isLoading: isFavoritesLoading } = useFavorites();
  const favoritedSessionIds = useMemo(
    () => new Set(favoritesData?.favorites.map((f) => f.sessionId) ?? []),
    [favoritesData?.favorites],
  );

  const queryStatus =
    activeTab === 'favorites' || activeTab === 'all' ? undefined : (activeTab as SessionStatus);

  const { data, isLoading: isSessionsLoading, error, refetch } = useSessionsList(queryStatus);

  // Note: session cleanup removed — was causing D1 OOM. Empty sessions
  // are now cleaned up lazily or can be triggered manually.

  useEffect(() => {
    refetch();
  }, [refetch, queryStatus]);

  const isLoading = isSessionsLoading || isFavoritesLoading;
  const { t } = useTranslation();
  const sessions = useMemo(() => data?.sessions ?? [], [data?.sessions]);

  const filteredSessions = sessions.filter((session) => {
    if (activeTab === 'favorites' && !favoritedSessionIds.has(session.id)) {
      return false;
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        session.name.toLowerCase().includes(q) ||
        (session.description && session.description.toLowerCase().includes(q))
      );
    }

    return true;
  });

  const navSide = panelSide === 'right' ? 'left' : 'right';
  const resultsCount = filteredSessions.length;

  const stats = useMemo(() => {
    const total = sessions.length;
    const active = sessions.filter((s) => s.status === 'active').length;
    const draft = sessions.filter((s) => s.status === 'draft').length;
    const archived = sessions.filter((s) => s.status === 'archived').length;
    const favorites = sessions.filter((s) => favoritedSessionIds.has(s.id)).length;

    return [
      { key: 'total', label: t.dashboard.all, value: total, icon: TrendingUp },
      { key: 'active', label: t.dashboard.active, value: active, icon: Sparkles },
      { key: 'draft', label: t.dashboard.draft, value: draft, icon: Workflow },
      { key: 'favorites', label: t.dashboard.favorites, value: favorites, icon: Star },
      { key: 'archived', label: t.dashboard.archived, value: archived, icon: ShieldCheck },
    ];
  }, [sessions, favoritedSessionIds, t.dashboard]);

  return (
    <div className={styles.outerWrapper}>
      {showTutorial && !tutorialDismissed && (
        <OnboardingTutorial
          type="dashboard"
          userId={currentUserId}
          onComplete={() => setTutorialDismissed(true)}
        />
      )}
      {!isMobile && navSide === 'left' && <SessionNavToolbar side="left" />}
      <div className={styles.container}>
        <>
          <title>Dashboard | Tasking.tech</title>
        </>

        <section className={styles.hero}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.heroTag}>
                <Rocket size={14} />
                Revenue-Ready Delivery Dashboard
              </div>
              <h1 className={styles.title}>{t.dashboard.title}</h1>
              <p className={styles.subtitle}>{t.dashboard.subtitle}</p>

              <div className={styles.statsGrid} aria-label="Session statistics">
                {stats.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.key} className={styles.statCard}>
                      <div className={styles.statCardHead}>
                        <Icon size={12} className={styles.statIcon} />
                        <span className={styles.statLabel}>{stat.label}</span>
                      </div>
                      <strong className={styles.statNumber}>{stat.value}</strong>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={styles.headerActions}>
              <Button
                variant="outline"
                onClick={() => setIsN8nPanelOpen(true)}
                title={t.dashboard.n8nWorkflows}
                className={styles.n8nButton}
                size="sm"
              >
                <Workflow size={14} />
                {t.dashboard.n8nWorkflows}
              </Button>
              <Button onClick={() => setIsCreateOpen(true)} size="sm">
                <Plus size={14} />
                {t.session.newSession}
              </Button>
            </div>
          </div>
        </section>

        {isN8nPanelOpen && <N8nPanel open={isN8nPanelOpen} onOpenChange={setIsN8nPanelOpen} />}

        <section className={styles.controls}>
          <div className={styles.controlsTop}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} size={14} />
              <Input
                placeholder={t.dashboard.searchPlaceholder}
                className={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className={styles.resultsPill}>
              <TrendingUp size={13} />
              <span>
                {resultsCount} session{resultsCount === 1 ? '' : 's'} shown
              </span>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className={styles.tabs}>
            <TabsList>
              <TabsTrigger value="all">{t.dashboard.all}</TabsTrigger>
              <TabsTrigger value="favorites" className={styles.tabWithIcon}>
                <Star size={12} className={styles.tabIcon} />
                {t.dashboard.favorites}
              </TabsTrigger>
              <TabsTrigger value="active">{t.dashboard.active}</TabsTrigger>
              <TabsTrigger value="draft">{t.dashboard.draft}</TabsTrigger>
              <TabsTrigger value="archived">{t.dashboard.archived}</TabsTrigger>
            </TabsList>
          </Tabs>
        </section>

        <section className={styles.gridShell}>
          {isLoading ? (
            <div className={styles.gridContainer}>
              <div className={styles.grid}>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className={styles.skeletonCard}>
                    <Skeleton style={{ height: '18px', width: '60%', marginBottom: '8px' }} />
                    <Skeleton style={{ height: '12px', width: '100%', marginBottom: '6px' }} />
                    <Skeleton style={{ height: '12px', width: '80%' }} />
                    <div
                      style={{
                        marginTop: 'auto',
                        paddingTop: '10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Skeleton style={{ height: '12px', width: '60px' }} />
                      <Skeleton style={{ height: '18px', width: '18px' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : error ? (
            <div className={styles.gridContainer}>
              <div className={styles.errorState}>
                <p>{t.dashboard.failedToLoad}</p>
              </div>
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className={styles.gridContainer}>
              <div className={styles.emptyState}>
                {!searchQuery && activeTab !== 'favorites' ? (
                  <div className={styles.emptyIcon}>
                    <img
                      src="/logo.png"
                      alt="Tasking.tech"
                      style={{ width: '48px', height: '48px', opacity: 0.5 }}
                    />
                  </div>
                ) : (
                  <div className={styles.emptyIcon}>
                    {activeTab === 'favorites' ? <Star size={24} /> : <Filter size={24} />}
                  </div>
                )}
                <h3>
                  {activeTab === 'favorites'
                    ? t.dashboard.noFavoriteSessions
                    : t.dashboard.noSessionsFound}
                </h3>
                <p>
                  {activeTab === 'favorites'
                    ? t.dashboard.markFavorite
                    : searchQuery
                      ? t.dashboard.tryAdjustSearch
                      : t.dashboard.getStarted}
                </p>
                {!searchQuery && activeTab !== 'favorites' && (
                  <Button onClick={() => setIsCreateOpen(true)} variant="outline">
                    {t.session.createSession}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.gridContainer}>
              <div className={styles.grid}>
                {filteredSessions.map((session) => {
                  const isFavorited = favoritedSessionIds.has(session.id);
                  return (
                    <div key={session.id} className={styles.cardWrapper}>
                      <SessionCard session={session} />
                      {isFavorited && (
                        <div className={styles.favoriteIndicator} title="Favorited">
                          <Star size={12} fill="currentColor" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        <CreateSessionDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} />
      </div>
      {!isMobile && navSide === 'right' && <SessionNavToolbar side="right" />}
    </div>
  );
}
